import type { CaptionLabelProps, Matcher } from "react-day-picker";
import { Chevron, DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

import type { ReactElement, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { fgPortalMount } from "../../lib/fgPortalMount";
import { MONTH_NAMES, formatYmdLong, formatYmdUsSlash } from "../../lib/dates";
import "./date-field.css";

type CalendarView = "days" | "months";

const DateFieldPickerContext = createContext<{
  openMonthPicker: () => void;
} | null>(null);

function ClickableCaptionLabel({ children, className, ...rest }: CaptionLabelProps) {
  const ctx = useContext(DateFieldPickerContext);
  return (
    <button
      type="button"
      {...rest}
      className={["rdp-caption_label", "fg-datefield__caption-btn", className]
        .filter(Boolean)
        .join(" ")}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        ctx?.openMonthPicker();
      }}>
      {children}
    </button>
  );
}

export type DateFieldProps = {
  value: string;
  onChange: (ymd: string) => void;
  /** Inclusive min `YYYY-MM-DD`. */
  min?: string;
  /** Inclusive max `YYYY-MM-DD`. */
  max?: string;
  /**
   * Dates before this `YYYY-MM-DD` are greyed (muted) but stay selectable — use
   * to signal "outside the plan window" without disabling.
   */
  dimBefore?: string;
  /** Dates after this `YYYY-MM-DD` are greyed (muted) but stay selectable. */
  dimAfter?: string;
  /** Days already covered by a purchased/delivered fueling plan — highlighted in the popover. */
  fueledDays?: ReadonlySet<string>;
  id?: string;
  disabled?: boolean;
  className?: string;
  /** Classes merged onto the calendar trigger (`fg-input` baseline). */
  inputClassName?: string;
  /** Secondary line under the trigger with a long-form date hint. */
  showLongFormat?: boolean;
  /**
   * How the trigger shows `value`: `iso` is `YYYY-MM-DD`; `usSlash` is `MM/DD/YYYY`.
   * Stored `onChange` values are always `YYYY-MM-DD`.
   */
  valuePresentation?: "iso" | "usSlash";
  /** Optional chevron/icon after the trigger label (toolbar dropdowns, etc.). */
  chevron?: ReactNode;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  /** Accessible name for the trigger button when there's no associated <label>. */
  "aria-label"?: string;
};

const CAL_W = 320;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Parses `YYYY-MM-DD` → local noon (stable for DayPicker matcher bounds). */
function ymdStringToLocalNoon(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return undefined;
  return new Date(y, mo - 1, d, 12, 0, 0, 0);
}

/** Selected grid day → stored `YYYY-MM-DD` (local wall date). */
function pickedLocalDateToYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function buildRangeMatchers(
  before?: string,
  after?: string
): Matcher | Matcher[] | undefined {
  const parts: Matcher[] = [];
  const beforeD = before?.trim() ? ymdStringToLocalNoon(before) : undefined;
  const afterD = after?.trim() ? ymdStringToLocalNoon(after) : undefined;
  if (beforeD) parts.push({ before: beforeD });
  if (afterD) parts.push({ after: afterD });
  if (parts.length === 0) return undefined;
  return parts.length === 1 ? parts[0]! : parts;
}

function monthBounds(min?: string, max?: string): { startYear: number; endYear: number } {
  const now = new Date().getFullYear();
  let startYear = now - 100;
  let endYear = now + 10;
  const minY = min?.trim().slice(0, 4);
  const maxY = max?.trim().slice(0, 4);
  if (minY && /^\d{4}$/.test(minY)) startYear = Number(minY);
  if (maxY && /^\d{4}$/.test(maxY)) endYear = Number(maxY);
  return { startYear, endYear };
}

function monthIsSelectable(year: number, month: number, min?: string, max?: string): boolean {
  const lastDay = new Date(year, month + 1, 0).getDate();
  const monthStart = `${year}-${pad2(month + 1)}-01`;
  const monthEnd = `${year}-${pad2(month + 1)}-${pad2(lastDay)}`;
  if (min?.trim() && monthEnd < min.trim()) return false;
  if (max?.trim() && monthStart > max.trim()) return false;
  return true;
}

function MonthYearPicker({
  year,
  selectedDate,
  min,
  max,
  onYearChange,
  onPickMonth
}: {
  year: number;
  selectedDate?: Date;
  min?: string;
  max?: string;
  onYearChange: (year: number) => void;
  onPickMonth: (month: number) => void;
}) {
  const { startYear, endYear } = monthBounds(min, max);
  const canPrev = year > startYear;
  const canNext = year < endYear;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  return (
    <div className="fg-datefield__month-picker">
      <div className="fg-datefield__year-nav">
        <button
          type="button"
          className="rdp-button_previous"
          aria-label="Previous year"
          disabled={!canPrev}
          onClick={() => canPrev && onYearChange(year - 1)}>
          <Chevron orientation="left" className="rdp-chevron" />
        </button>
        <span className="fg-datefield__year-label">{year}</span>
        <button
          type="button"
          className="rdp-button_next"
          aria-label="Next year"
          disabled={!canNext}
          onClick={() => canNext && onYearChange(year + 1)}>
          <Chevron orientation="right" className="rdp-chevron" />
        </button>
      </div>
      <div className="fg-datefield__month-grid" role="grid" aria-label={`Months in ${year}`}>
        {MONTH_NAMES.map((label, month) => {
          const enabled = monthIsSelectable(year, month, min, max);
          const selected =
            !!selectedDate &&
            selectedDate.getFullYear() === year &&
            selectedDate.getMonth() === month;
          const isCurrent = year === currentYear && month === currentMonth;
          return (
            <button
              key={label}
              type="button"
              role="gridcell"
              className={[
                "fg-datefield__month-cell",
                selected ? "fg-datefield__month-cell--selected" : "",
                !selected && isCurrent ? "fg-datefield__month-cell--current" : "",
                !enabled ? "fg-datefield__month-cell--disabled" : ""
              ]
                .filter(Boolean)
                .join(" ")}
              disabled={!enabled}
              onClick={() => onPickMonth(month)}>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Calendar date field using **react-day-picker** (portal + fixed position so it
 * isn’t clipped inside modal scroll areas). Value is controlled `YYYY-MM-DD`.
 */
export function DateField({
  value,
  onChange,
  min,
  max,
  dimBefore,
  dimAfter,
  fueledDays,
  id,
  disabled,
  className = "",
  inputClassName = "",
  showLongFormat = true,
  valuePresentation = "iso",
  chevron,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel
}: DateFieldProps): ReactElement {
  const autoId = useId();
  const fieldId = id ?? `fg-date-${autoId}`;
  const portalTarget = fgPortalMount() ?? (typeof document !== "undefined" ? document.body : null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [calendarView, setCalendarView] = useState<CalendarView>("days");
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());

  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const trimmed = value.trim();
  const selectedDate = useMemo(
    () => (trimmed.length ? ymdStringToLocalNoon(trimmed) : undefined),
    [trimmed]
  );

  useEffect(() => {
    if (!open) {
      setCalendarView("days");
      return;
    }
    const seed = selectedDate ?? new Date();
    setVisibleMonth(seed);
    setPickerYear(seed.getFullYear());
  }, [open, selectedDate]);

  const longHint = showLongFormat && trimmed.length ? formatYmdLong(trimmed) : null;

  const disabledMatchers = useMemo(() => buildRangeMatchers(min, max), [min, max]);
  const mutedMatchers = useMemo(
    () => buildRangeMatchers(dimBefore, dimAfter),
    [dimBefore, dimAfter]
  );

  const modifiers = useMemo(() => {
    const next: Record<string, Matcher | Matcher[]> = {};
    if (mutedMatchers) next.fgOutsideWindow = mutedMatchers;
    if (fueledDays?.size) {
      next.fgFueled = (date: Date) => fueledDays.has(pickedLocalDateToYmd(date));
    }
    return Object.keys(next).length ? next : undefined;
  }, [mutedMatchers, fueledDays]);

  const modifiersClassNames = useMemo(() => {
    const next: Record<string, string> = {};
    if (mutedMatchers) next.fgOutsideWindow = "fg-datefield__day--muted";
    if (fueledDays?.size) next.fgFueled = "fg-datefield__day--fueled";
    return Object.keys(next).length ? next : undefined;
  }, [mutedMatchers, fueledDays]);

  function reposition() {
    const el = btnRef.current;
    if (!el || typeof window === "undefined") return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const gap = 6;
    let left = Math.round(r.left);
    const top = Math.round(r.bottom + gap);
    const maxLeft = vw - CAL_W - 16;
    if (left > maxLeft) left = Math.max(16, maxLeft);
    if (left < 16) left = 16;
    setPos({ top, left });
  }

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [open, trimmed]);

  useEffect(() => {
    if (!open) return;
    const scroll = () => reposition();
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", scroll);
    return () => {
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", scroll);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open]);

  useEffect(() => {
    if (!open || !portalTarget) return;
    const onMd = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMd);
    return () => document.removeEventListener("mousedown", onMd);
  }, [open, portalTarget]);

  function handleSelect(selected: Date | undefined) {
    if (!selected || disabled) return;
    onChange(pickedLocalDateToYmd(selected));
    setOpen(false);
  }

  const openMonthPicker = useCallback(() => {
    setPickerYear(visibleMonth.getFullYear());
    setCalendarView("months");
  }, [visibleMonth]);

  const pickerContext = useMemo(() => ({ openMonthPicker }), [openMonthPicker]);

  const popover =
    open && portalTarget && !disabled ? (
      <div className="fg-datefield-portal-root">
        <div className="fg-datefield__backdrop" aria-hidden onMouseDown={() => setOpen(false)} />
        <div
          ref={panelRef}
          id={`${fieldId}-panel`}
          role="dialog"
          aria-label={calendarView === "months" ? "Choose month" : "Choose calendar date"}
          className="fg-datefield__popover"
          tabIndex={-1}
          style={{
            top: pos.top,
            left: pos.left,
            width: CAL_W,
            maxWidth: "min(320px, calc(100vw - 24px))",
            boxSizing: "border-box"
          }}>
          <DateFieldPickerContext.Provider value={pickerContext}>
            {calendarView === "months" ? (
              <MonthYearPicker
                year={pickerYear}
                selectedDate={selectedDate}
                min={min}
                max={max}
                onYearChange={setPickerYear}
                onPickMonth={(month) => {
                  setVisibleMonth(new Date(pickerYear, month, 1, 12, 0, 0, 0));
                  setCalendarView("days");
                }}
              />
            ) : (
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleSelect}
                disabled={disabledMatchers}
                modifiers={modifiers}
                modifiersClassNames={modifiersClassNames}
                month={visibleMonth}
                onMonthChange={setVisibleMonth}
                weekStartsOn={1}
                navLayout="around"
                animate
                components={{ CaptionLabel: ClickableCaptionLabel }}
              />
            )}
          </DateFieldPickerContext.Provider>
        </div>
      </div>
    ) : null;

  const triggerLabel = (() => {
    if (!trimmed.length) return "Pick a date";
    if (valuePresentation !== "usSlash") return trimmed;
    const slash = formatYmdUsSlash(trimmed);
    return slash !== "" ? slash : trimmed;
  })();

  return (
    <div className={["fg-datefield", className].filter(Boolean).join(" ")}>
      <button
        ref={btnRef}
        id={fieldId}
        type="button"
        className={[
          "fg-input",
          "fg-datefield__trigger",
          ariaInvalid ? "fg-datefield__trigger--invalid" : "",
          disabled ? "fg-datefield__trigger--disabled" : "",
          inputClassName
        ]
          .filter(Boolean)
          .join(" ")}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? `${fieldId}-panel` : undefined}
        aria-haspopup="dialog"
        disabled={disabled ?? false}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}>
        <span className="fg-datefield__trigger-text">{triggerLabel}</span>
        <span className="fg-datefield__chev" aria-hidden>
          {chevron ?? "⌄"}
        </span>
      </button>
      {longHint ? (
        <span className="fg-datefield__hint">{longHint}</span>
      ) : showLongFormat && trimmed.length === 0 ? (
        <span className="fg-datefield__hint fg-datefield__hint--muted">
          Select a date from the calendar
        </span>
      ) : null}
      {popover && portalTarget ? createPortal(popover, portalTarget) : null}
    </div>
  );
}
